// src/pages/MentalPlus/DisplayIsland.tsx
import React from "react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../contexts/ConfigContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import NextPrevButtons from "../../components/molecules/NextPrevButtons";
import { useAuth } from "../../contexts/AuthContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import Info from "../../components/atoms/ErrorComponent";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import {
  TITLE_MAP,
  SEQUENCE_MAP,
  INLINE_SEQUENCE_MAP,
  ALL_ISLANDS,
  WeatherKey,
} from "./IslandRegistry";
import { useTodayScores } from "../../hooks/useScoredToday";
import InlineQuestion from "../../components/organisms/InlineQuestion/InlineQuestion";
import { getApiFullSequence, FullSequenceResponse } from "../../services/questionnaire.service";
import { useFetch } from "../../hooks/useFetch";
import { defaultAnswerValueByType, formatAnswers, getAnswerValue } from "../../helper/answer";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";

const DisplayIsland: React.FC = () => {
  const { sequences, loading, error } = useResonanceData();
  const history = useHistory();
  const { study_slug, title: key } = useParams<{ study_slug: string; title: string }>();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study } = useConfig();
  const { user } = useAuth();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();
  const { hasScoreToday, todayScores } = useTodayScores();

  // ✅ NEW: use renamed titles from context
  const { getTitle } = useIslandVisibility();
  const displayTitle = (key ? getTitle(key) : "") || TITLE_MAP[key] || key;

  // ✅ Always call hooks at top level
  useIonViewWillEnter(() => {
    if (key) {
      setResonanceLayout({
        disableHeader: false,
        disableNav: true,
        backgroundColor: "white",
        title: displayTitle, // ✅ renamed title
        welcomePage: false,
      });
    }
  });

  // 🧩 Determine inline sequence info
  const inlineSeqId = INLINE_SEQUENCE_MAP[key];
  const inlineSeq = sequences?.find((s) => s.sequence_id === inlineSeqId);
  const hasInlineData = !!(inlineSeq && inlineSeq.components?.length);

  // 🧩 Safe fetch control
  const fetchEnabled = Boolean(!loading && inlineSeqId && inlineSeq?.id && hasInlineData);
  const userSequenceId = inlineSeq?.id ?? 0;
  const questionnaireId = inlineSeq?.components?.[0]?.questionnaire_id ?? 0;
  const componentId = inlineSeq?.components?.[0]?.id ?? 0;

  const { data, isLoading, isError } = useFetch<FullSequenceResponse>(
    `inline-sequence:${userSequenceId}`,
    getApiFullSequence,
    {
      studyId: study.id,
      userId: user.id,
      sequenceId: userSequenceId,
      lang: language,
    },
    { enabled: fetchEnabled }
  );

  const inlineQuestionnaire = data?.questionnaires?.find(
    (q) => q.id.toString() === questionnaireId.toString()
  );
  const firstQuestion = inlineQuestionnaire?.questions?.[0];

  const formatted = formatAnswers(data?.answers?.answers ?? []);
  const previousAnswer = formatted.find((a) => a.question_id === firstQuestion?.id);

  const initialValue = previousAnswer
    ? getAnswerValue(firstQuestion.type, previousAnswer)
    : defaultAnswerValueByType[firstQuestion?.type ?? "CHECKBOX"];

  // 🧩 Handle early return safely AFTER all hooks
  if (loading) return <ResonanceLoader />;
  if (error || !sequences?.length) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }

  if (!inlineSeqId) {
    console.warn(`No sequence mapping found for key "${key}"`);
    return (
      <Info
        title={t("common_nodata_title")}
        description={`Aucun questionnaire associé à ${key}.`}
        actionText={""}
      />
    );
  }

  if (!hasInlineData) {
    console.warn("No inline sequence or components yet for", key);
    return <ResonanceLoader />;
  }

  // 🧩 UI helpers
  const def = ALL_ISLANDS[key];

  // 👇 which sequence's VISUAL CONFIG drives the icon?
  const iconSourceCfg = def.iconFromInline ? def.inline ?? def.main : def.main;
  const iconSequenceId = iconSourceCfg?.sequenceId;

  const hasScore = !!iconSequenceId && hasScoreToday.has(iconSequenceId);
  const todaysScore = iconSequenceId != null ? todayScores.get(iconSequenceId) : undefined;

  // 👇 if the driving sequence has a sprite scale, use it
  const iconScale = iconSourceCfg?.scale?.mode === "sprite" ? iconSourceCfg.scale : undefined;

  let Icon = def.Component;

  if (def.weatherSprites) {
    if (!hasScore) {
      Icon = def.weatherSprites.nuit ?? def.Component;
    } else if (todaysScore != null && iconScale) {
      const spriteKey = iconScale.map(todaysScore) as WeatherKey;
      Icon = def.weatherSprites[spriteKey] ?? def.Component;
    } else {
      Icon = def.weatherSprites.soleil ?? def.Component;
    }
  }

  const iconSize = 70;
  const svgStyle = {
    width: iconSize,
    height: iconSize,
    borderRadius: "50px",
  };

  const goPrevious = () => ionRouter.push(`/${study_slug}/tabs/home`, "none", "push");
  const goToVideo = (contentType: "Explications" | "Solutions") =>
    history.push(`/${study_slug}/tabs/home/island/${key}/${contentType}`);
  const goToEvolution = () => history.push(`/${study_slug}/tabs/home/island/${key}/evolution`);

  const goToQuestionnaire = () => {
    const seqId = SEQUENCE_MAP[key];
    if (!seqId) {
      console.warn(`No sequence mapping found for key "${key}"`);
      return;
    }

    const seq = sequences.find((s) => s.sequence_id === seqId);
    if (!seq) return;

    const nextComponent = seq.components.find((c) => !c.is_done);
    if (!nextComponent) {
      console.log("All components are done, nothing to launch.");
      return;
    }

    history.push(
      `/${study_slug}/tabs/home/island/${key}/sequences/${seq.id}/components/${nextComponent.id}`
    );

    setResonanceLayout({
      disableHeader: false,
      disableNav: true,
      backgroundColor: "white",
      title: t("questionnaire_title"),
      welcomePage: false,
    });
  };

  // 🧩 Render
  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        <MainCard>
          <IslandIconWrapper>{Icon ? <Icon style={svgStyle} /> : null}</IslandIconWrapper>
          <CardContent>
            {/* ✅ renamed title */}
            <Title>{displayTitle}</Title>
            <QuestionButton onClick={goToQuestionnaire}>Quel temps fait-il ?</QuestionButton>
          </CardContent>
        </MainCard>

        {isLoading && <ResonanceLoader />}
        {isError && (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
            actionText={""}
          />
        )}
        {firstQuestion && (
          <InlineQuestion
            question={firstQuestion}
            questionnaireId={questionnaireId}
            userSequenceId={userSequenceId}
            componentId={componentId}
            questionnaireData={inlineQuestionnaire}
            initialValue={initialValue}
            autoSubmit={true}
          />
        )}

        <ButtonsColumn>
          <ActionButton onClick={() => goToVideo("Explications")}>
            <span role="img" aria-label="explications">
              🗣️
            </span>
            <ButtonText>Explications</ButtonText>
          </ActionButton>
          <ActionButton onClick={() => goToVideo("Solutions")}>
            <span role="img" aria-label="solutions">
              💡
            </span>
            <ButtonText>Solutions</ButtonText>
          </ActionButton>
          <ActionButton onClick={goToEvolution}>
            <span role="img" aria-label="evolution">
              📈
            </span>
            <ButtonText>Evolution</ButtonText>
          </ActionButton>
        </ButtonsColumn>

        <div style={{ marginTop: "15px", alignSelf: "flex-start" }}>
          <NextPrevButtons onPreviousClick={goPrevious} />
        </div>
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default DisplayIsland;

//
// ----- STYLES -----
//
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  padding-top: 15px;
`;

const MainCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 340px;
  margin-top: 30px;
  padding-top: 52px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IslandIconWrapper = styled.div`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 80px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  svg {
    width: 65px;
    height: 65px;
    object-fit: contain;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.div`
  font-size: 1.22rem;
  font-weight: 600;
  color: #232323;
  margin-bottom: 16px;
`;

const QuestionButton = styled.button`
  background: #3b8eff;
  color: #fff;
  font-weight: 500;
  font-size: 1rem;
  padding: 10px 20px;
  border: none;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  margin-top: 0;
  cursor: pointer;
  transition: background 0.2s;
  &:hover,
  &:focus {
    background: #357eea;
  }
`;

const ButtonsColumn = styled.div`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ActionButton = styled.button`
  width: 100%;
  background: #f7f7fb;
  color: #222;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.02rem;
  font-weight: 500;
  border: none;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(50, 50, 93, 0.04);
  cursor: pointer;
  transition: background 0.16s;

  &:hover,
  &:focus {
    background: #ebedf5;
  }
  span[role="img"] {
    font-size: 1.4rem;
    margin-right: 8px;
  }
`;

const ButtonText = styled.span`
  flex: 1;
  text-align: left;
`;
